import { useQuery } from "@tanstack/react-query";
import type { Coordinate } from "@/entities/tracking";
import { getAgent } from "@/features/agent/services/agentService";
import {
  useAgentLiveLocation,
  useRealtimeConnected,
  type AgentLocationUpdatedPayload,
} from "@/shared/realtime";

// Position de la Main, avec REPLI REST — le socket n'est plus le seul chemin.
//
// ## Le defaut que ce hook corrige
// La carte de suivi ne lisait la position QUE via l'evenement socket
// `agent.location.updated`. Socket coupe — reseau mobile qui bascule, onglet
// reveille, proxy qui ferme la connexion — et le client restait indefiniment
// sur « En attente de la position de la Main… », alors meme que l'agent
// emettait correctement et que le backend connaissait sa position.
//
// Le bandeau « Connexion live perdue · mise a jour ralentie » decrivait deja ce
// cas, mais le repli qu'il annonce ne portait QUE sur le statut de la commande :
// la position, elle, n'avait aucun recours.
//
// `GET /agents/{id}` expose `latitude`/`longitude`. On s'en sert comme filet :
// moins frais que le socket, infiniment mieux qu'un ecran vide.

/** Au-dela, une position socket est jugee perimee et le repli REST reprend. */
const LIVE_STALE_AFTER_MS = 45_000;
/** Cadence du repli. Assez lent pour ne pas marteler l'API, assez vif pour suivre une moto. */
const REST_POLL_MS = 15_000;

export type AgentPositionSource = "live" | "rest";

export type AgentPositionState = {
  coord: Coordinate | null;
  /** D'ou vient la position affichee — sert a nuancer le discours a l'ecran. */
  source: AgentPositionSource | null;
  /** Le socket repond-il ? (false => on affiche un suivi degrade, pas une panne) */
  isLive: boolean;
  /**
   * Quand cette position a-t-elle ete PRISE, en ms — ou `null` si on l'ignore.
   *
   * C'est ce qui permet a l'ecran de dater la position plutot que d'affirmer
   * « en direct » au-dessus d'un marqueur fige (cf. `lib/positionFreshness`).
   *
   * ⚠️ `null` sur le repli REST, et ce n'est pas un oubli : `GET /agents/{id}`
   * rend `latitude`/`longitude` mais AUCUNE date de releve. Son `updatedAt`
   * date la fiche, pas la position — s'en servir daterait une position de
   * l'instant ou quelqu'un a modifie un nom. Mieux vaut ne rien affirmer.
   */
  updatedAt: number | null;
};

/**
 * Position de la Main à partir d'un flux live DÉJÀ OBTENU, avec repli REST.
 *
 * ## Pourquoi la position arrive de l'extérieur
 * R7/R8 du contrat du 2026-08-26 : pour suivre une course, `trip:{id}` suffit —
 * la position y est désormais portée, et le client s'y trouve déjà. Passer par
 * `agent:{id}` obligeait à connaître l'identifiant de la Main (inconnu tant
 * qu'aucune n'est affectée) et laissait l'app dans la salle d'une personne
 * APRÈS la fin de la course, à recevoir ses positions sur d'autres missions.
 *
 * Ce hook ne choisit donc plus sa source : il reçoit la position live de qui
 * l'écoute — salle de course pour une course, salle d'agent pour une commande —
 * et ne s'occupe que du repli.
 */
export function useAgentPositionFrom(
  liveLoc: AgentLocationUpdatedPayload | null,
  agentId: string | undefined,
  enabled = true,
): AgentPositionState {
  const isSocketConnected = useRealtimeConnected();

  // Une position socket cesse d'etre digne de confiance en vieillissant : le
  // socket peut rester « connecte » sans plus rien emettre (agent en veille,
  // onglet suspendu par le navigateur). On date donc la derniere reception.
  const liveAt = liveLoc ? Date.parse(liveLoc.timestamp) : NaN;
  const hasFreshLive =
    Boolean(liveLoc) && Number.isFinite(liveAt) && Date.now() - liveAt < LIVE_STALE_AFTER_MS;

  // Le repli ne tourne que s'il sert a quelque chose : pas de position live
  // fraiche. Des que le socket reprend, le polling s'arrete de lui-meme.
  const restQuery = useQuery({
    queryKey: ["agent", "position", agentId],
    queryFn: () => getAgent(agentId!),
    enabled: enabled && Boolean(agentId) && !hasFreshLive,
    refetchInterval: REST_POLL_MS,
    // Pas d'insistance : l'agent peut n'avoir jamais publie de position.
    retry: 1,
    staleTime: 0,
  });

  if (!enabled || !agentId) {
    return { coord: null, source: null, isLive: isSocketConnected, updatedAt: null };
  }

  if (hasFreshLive && liveLoc) {
    return {
      coord: { latitude: liveLoc.latitude, longitude: liveLoc.longitude },
      source: "live",
      isLive: true,
      updatedAt: liveAt,
    };
  }

  const profile = restQuery.data;
  if (profile?.latitude != null && profile.longitude != null) {
    return {
      coord: { latitude: profile.latitude, longitude: profile.longitude },
      source: "rest",
      isLive: isSocketConnected,
      updatedAt: null,
    };
  }

  // Une position socket PERIMEE reste meilleure que rien : elle situe la Main,
  // meme approximativement, la ou le vide ne dit rien du tout.
  if (liveLoc) {
    return {
      coord: { latitude: liveLoc.latitude, longitude: liveLoc.longitude },
      source: "live",
      isLive: isSocketConnected,
      // Perimee, mais DATEE : c'est precisement l'age qu'il faut montrer.
      updatedAt: Number.isFinite(liveAt) ? liveAt : null,
    };
  }

  return { coord: null, source: null, isLive: isSocketConnected, updatedAt: null };
}

/**
 * Position de la Main via SA salle (`agent:{id}`).
 *
 * ⚠️ Réservé au suivi d'une COMMANDE, qui n'a pas de salle de course où se
 * rattacher. Pour une COURSE, passez par `useTripLiveMap` : la salle
 * `trip:{id}` porte la position et se vide d'elle-même à la fin de la course
 * (R7/R9).
 *
 * `enabled` à `false` QUITTE la salle — c'est ce qui empêche de continuer à
 * suivre un livreur qui ne travaille plus pour vous.
 */
export function useAgentPosition(
  agentId: string | undefined,
  enabled = true,
): AgentPositionState {
  const liveLoc = useAgentLiveLocation(agentId, enabled);
  return useAgentPositionFrom(liveLoc, agentId, enabled);
}
